import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, CreditCard, Pencil, Trash2, Save, X, Search } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API_URL}/api/cms/payment-terms`;

const PaymentTerms = () => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState({ open: false, mode: 'create', data: null });
  const { toast } = useToast();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${ENDPOINT}/?active_only=false`);
      setTerms(r.data || []);
    } catch (_) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to load payment terms' });
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search) return terms;
    const s = search.toLowerCase();
    return terms.filter((t) =>
      (t.code || '').toLowerCase().includes(s)
      || (t.label || '').toLowerCase().includes(s)
      || (t.body || '').toLowerCase().includes(s)
    );
  }, [terms, search]);

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.label}"?`)) return;
    try {
      await axios.delete(`${ENDPOINT}/${t.id}`);
      toast({ title: 'Deleted', description: t.label });
      fetchAll();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.response?.data?.detail || 'Delete failed' });
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="payment-terms-page">
      <BackButton />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42] flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-[#023E8A]" /> Payment Terms
          </h1>
          <p className="text-sm text-[#6C757D] mt-1">
            Register reusable payment-condition snippets. Used by the RFQ/LOI modal and Invoice forms as an "Insert" dropdown.
          </p>
        </div>
        <Button onClick={() => setEditor({ open: true, mode: 'create', data: null })} className="bg-[#023E8A] hover:bg-[#0353A4] text-white" data-testid="payment-terms-new-btn">
          <Plus className="w-4 h-4 mr-2" /> New Term
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
          placeholder="Search code, label or body…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="payment-terms-search"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-[#6C757D]">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-[#6C757D]">No payment terms yet.</div>
          ) : (
            <div className="divide-y">
              {filtered.map((t) => (
                <div key={t.id} className="p-4 flex items-start justify-between hover:bg-gray-50" data-testid={`payment-term-row-${t.code}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#2B2D42]">{t.label}</span>
                      <code className="text-xs text-[#6C757D] bg-gray-100 px-2 py-0.5 rounded">{t.code}</code>
                      {t.is_default && <Badge className="bg-green-100 text-green-800 border border-green-300">default</Badge>}
                      {!t.is_active && <Badge className="bg-gray-200 text-gray-700">inactive</Badge>}
                      <span className="text-[11px] text-gray-400">order: {t.sort_order ?? 100}</span>
                    </div>
                    <p className="text-sm text-[#374151] mt-1 whitespace-pre-wrap">{t.body}</p>
                    {t.description && <p className="text-xs text-[#6C757D] mt-1 italic">{t.description}</p>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => setEditor({ open: true, mode: 'edit', data: t })} data-testid={`payment-term-edit-${t.code}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(t)} data-testid={`payment-term-delete-${t.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {editor.open && (
        <Editor
          mode={editor.mode}
          term={editor.data}
          onClose={() => setEditor({ open: false, mode: 'create', data: null })}
          onSaved={() => { setEditor({ open: false, mode: 'create', data: null }); fetchAll(); }}
        />
      )}
    </div>
  );
};


const Editor = ({ mode, term, onClose, onSaved }) => {
  const { toast } = useToast();
  const isEdit = mode === 'edit';
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: term?.code || '',
    label: term?.label || '',
    body: term?.body || '',
    description: term?.description || '',
    is_default: !!term?.is_default,
    is_active: term?.is_active !== false,
    sort_order: term?.sort_order ?? 100,
  });

  const handleSave = async () => {
    if (!form.code.trim() || !form.label.trim() || !form.body.trim()) {
      toast({ variant: 'destructive', title: 'Missing fields', description: 'Code, Label and Body are required' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, code: form.code.trim(), sort_order: Number(form.sort_order) || 100 };
      if (isEdit) {
        const { code, ...patchable } = payload;
        await axios.patch(`${ENDPOINT}/${term.id}`, patchable);
        toast({ title: 'Saved', description: form.label });
      } else {
        await axios.post(`${ENDPOINT}/`, payload);
        toast({ title: 'Created', description: form.label });
      }
      onSaved();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Save failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="payment-term-editor">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[94vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-lg font-bold text-[#2B2D42]">{isEdit ? 'Edit Payment Term' : 'New Payment Term'}</h3>
          <button onClick={onClose} data-testid="payment-term-editor-close"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="flex-1 overflow-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#6C757D]">Code *</label>
              <input
                className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                placeholder="e.g. lc_full_on_shipping"
                value={form.code}
                disabled={isEdit}
                onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                data-testid="payment-term-code"
              />
            </div>
            <div>
              <label className="text-xs text-[#6C757D]">Sort order</label>
              <input
                type="number"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#6C757D]">Label (shown in dropdown) *</label>
            <input
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="LC + Full Payment upon Shipping & SGS Inspection"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              data-testid="payment-term-label"
            />
          </div>
          <div>
            <label className="text-xs text-[#6C757D]">Body (inserted into Terms) *</label>
            <textarea
              rows={5}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="Payment terms: LC, followed by Full Payment upon Shipping Documents and Physical Inspection or SGS or Similar."
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="payment-term-body"
            />
          </div>
          <div>
            <label className="text-xs text-[#6C757D]">Description (internal — not visible on documents)</label>
            <input
              className="w-full px-3 py-2 border rounded-lg text-sm"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} /> Default
            </label>
          </div>
        </div>
        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-white">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#023E8A] hover:bg-[#0353A4] text-white" data-testid="payment-term-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create term')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentTerms;
